/**
 * Live ETA Service
 * ----------------
 * On-demand recomputation of a driver's REMAINING-stop ETAs from their CURRENT
 * GPS position. Reuses the routing engine (Google Directions + traffic, with the
 * built-in simulate fallback) and each stop's already-stored dwell_minutes.
 *
 * Design notes:
 *  - The live projection is a DISPLAY tool for dispatch. It does NOT write
 *    load_stops at all. The planned baseline is load_stops.planned_arrival_at
 *    (stamped only when a route is built/rebuilt; __PLAN_BASELINE__), falling
 *    back to estimated_arrival(_time). We only READ it, to show a delta
 *    ("live vs planned").
 *  - Origin of the recompute is the truck's latest GPS ping. The cascade starts
 *    at the PING time, not "now": a ping is where the truck WAS. If the ping is
 *    old, travel time already elapsed is credited (arrival never lands in the
 *    past), and beyond STALE_MAX_MINUTES we refuse to project at all - a 40-min
 *    old fix says nothing useful about where the truck is (audit S9).
 *        arrival(i) = max(now, t_ping + sum(leg travel to i))
 *        departure(i) = arrival(i) + dwell(i)
 *  - A stop the truck is currently AT (arrival stamped, no departure) is kept in
 *    the cascade with zero travel and only its REMAINING dwell.
 *  - Order of the remaining stops follows the driver's plan across ALL their
 *    active loads (stops sorted by the planned estimated_arrival the routing
 *    engine stamped), so an interleaved day (pickup A, pickup B, deliver A,
 *    deliver B) no longer cascades load A's delivery as if B didn't exist
 *    (audit S12). Stops without a plan fall back to pickups-then-deliveries.
 *  - All DB timestamps go through parseDbTime(): the hot columns are naive UTC
 *    and `new Date()` would read them as local time (4-5 h off in Ontario).
 */

import { supabase } from '../supabaseClient';
import { calculateRoute, geocodeAddress, getRoutingConfig } from './routingEngineService';
import { parseDbTime } from '../utils/dbTime';
import { dwellMinutesForStop } from './dwellService';

const DONE_LOAD_STATUSES = ['complete', 'cancelled', 'ready_to_bill', 'billed', 'archived'];

/** GPS age ladder (same thresholds as gpsTrackingService: LIVE<=5, STALE<=30, else OFFLINE). */
export const LIVE_MAX_MINUTES = 5;
export const STALE_MAX_MINUTES = 30;

const fmtTime = (date) =>
  date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const arrivalOf = (s) => s.actual_arrival_time || s.actual_arrival || null;
const departureOf = (s) => s.actual_departure_time || s.actual_departure || null;

/** Departed / completed - nothing left to project for this stop. */
const stopIsDone = (s) =>
  !!(s.completed || departureOf(s) || s.status === 'completed');

/** Truck is at this stop right now (arrived, not yet departed). */
const stopInProgress = (s) => !stopIsDone(s) && !!arrivalOf(s);

/**
 * Plan order across loads: by planned arrival when the routing engine stamped
 * one; otherwise pickups-then-deliveries by sequence, in-progress stop first.
 */
// __PLAN_BASELINE__ the plan a stop is measured against: the frozen route-build
// baseline (planned_arrival_at) first, the older stamped ETA columns after.
const planOf = (s) => s.planned_arrival_at || s.estimated_arrival_time || s.estimated_arrival;

// The load's cargo rides on each stop so the dwell resolver can use the global
// cargo-type time / size tier - the same inputs the route builder planned with.
const CARGO_COLS = 'commodity, weight_kg, length_cm, width_cm, height_cm';
const cargoLoad = (l) => (l ? { commodity: l.commodity, weight_kg: l.weight_kg, length_cm: l.length_cm, width_cm: l.width_cm, height_cm: l.height_cm } : null);

const orderStops = (stops) => {
  const typeKey = (s) => (s.stop_type === 'pickup' ? 0 : 1);
  const planMs = (s) => {
    const d = parseDbTime(planOf(s));
    return d ? d.getTime() : null;
  };
  return [...stops].sort((a, b) => {
    const ia = stopInProgress(a) ? 0 : 1;
    const ib = stopInProgress(b) ? 0 : 1;
    if (ia !== ib) return ia - ib;
    const pa = planMs(a), pb = planMs(b);
    if (pa != null && pb != null && pa !== pb) return pa - pb;
    if ((pa == null) !== (pb == null)) return pa == null ? 1 : -1; // planned before unplanned
    return typeKey(a) - typeKey(b) || (a.stop_sequence || 0) - (b.stop_sequence || 0);
  });
};

const ageLabel = (ageMinutes) => {
  if (ageMinutes == null) return 'NO DATA';
  if (ageMinutes <= LIVE_MAX_MINUTES) return 'LIVE';
  if (ageMinutes <= STALE_MAX_MINUTES) return 'STALE';
  return 'OFFLINE';
};

/** Latest GPS ping for a truck (preferred) or driver. Returns {lat,lng,timestamp,ageMinutes,status} | null */
export const fetchLatestGps = async ({ truckId = null, driverId = null } = {}) => {
  try {
    if (!truckId && !driverId) return null;
    let q = supabase
      .from('gps_locations')
      .select('latitude, longitude, timestamp, truck_id, driver_id, accuracy_meters')
      .order('timestamp', { ascending: false })
      .limit(1);
    q = truckId ? q.eq('truck_id', truckId) : q.eq('driver_id', driverId);
    const { data, error } = await q;
    if (error || !data || !data[0]) return null;
    const row = data[0];
    if (row.latitude == null || row.longitude == null) return null;
    const at = parseDbTime(row.timestamp);
    const ageMinutes = at ? Math.max(0, Math.round((Date.now() - at.getTime()) / 60000)) : null;
    return {
      lat: row.latitude,
      lng: row.longitude,
      timestamp: row.timestamp,
      atMs: at ? at.getTime() : null,
      accuracy: row.accuracy_meters,
      ageMinutes,
      status: ageLabel(ageMinutes),
    };
  } catch (e) {
    console.error('fetchLatestGps error:', e);
    return null;
  }
};

/** Ensure a stop has numeric lat/lng, geocoding its address if needed. */
const ensureCoords = async (stop) => {
  let lat = stop.latitude != null ? stop.latitude : stop.lat;
  let lng = stop.longitude != null ? stop.longitude : stop.lng;
  if ((lat == null || lng == null) && stop.address) {
    const g = await geocodeAddress(stop.address);
    if (g) { lat = g.lat; lng = g.lng; }
  }
  return { ...stop, lat, lng };
};

/**
 * Cascade live ETAs over an ordered list of remaining stops from a GPS origin.
 * Returns [{ id, load_id, stop_type, stop_sequence, address, etaISO, etaFormatted,
 *            minutesFromNow, dwellMinutes, planISO, deltaMinutes, inProgress }]
 */
const cascadeFromGps = async (gps, remaining) => {
  const withCoords = [];
  for (const s of remaining) withCoords.push(await ensureCoords(s));
  const routable = withCoords.filter((s) => s.lat != null && s.lng != null);
  if (routable.length === 0) return [];

  const nowMs = Date.now();
  const defaultDwell = getRoutingConfig().defaultDwellMinutes || 30;
  // Same dwell source as the planner, so the live projection and the plan can
  // actually be compared (both go through dwell_time_settings).
  const dwellOf = new Map();
  for (const s of routable) {
    try { dwellOf.set(s.id, await dwellMinutesForStop(s)); } catch (e) { /* falls back below */ }
  }

  // The stop the truck is sitting at needs no travel leg; route from it.
  const atStop = routable[0] && stopInProgress(routable[0]) ? routable[0] : null;
  const origin = atStop
    ? { lat: atStop.lat, lng: atStop.lng, address: null }
    : { lat: gps.lat, lng: gps.lng, address: null };
  const driveStops = atStop ? routable.slice(1) : routable;
  const route = driveStops.length > 0 ? await calculateRoute([origin, ...driveStops]) : null;
  const legs = (route && route.legs) || [];

  const out = [];
  // Start the clock at the ping (or arrival) time, never in the future.
  let t = Math.min(nowMs, gps.atMs != null ? gps.atMs : nowMs);
  let prev = origin;
  for (let i = 0; i < routable.length; i++) {
    const s = routable[i];
    let arrivalMs;
    let dwell = dwellOf.get(s.id) ?? (s.dwell_minutes != null ? s.dwell_minutes : defaultDwell);

    if (atStop && i === 0) {
      const arrivedAt = parseDbTime(arrivalOf(s));
      arrivalMs = arrivedAt ? arrivedAt.getTime() : nowMs;
      const elapsedMin = Math.max(0, (nowMs - arrivalMs) / 60000);
      dwell = Math.max(0, dwell - elapsedMin); // remaining dwell only
      t = Math.max(nowMs, arrivalMs);
    } else {
      const legIdx = atStop ? i - 1 : i;
      const leg = legs[legIdx];
      const travelSec = leg
        ? (leg.durationInTrafficSeconds || leg.durationSeconds || 0)
        : (haversineKm(prev.lat, prev.lng, s.lat, s.lng) / 70) * 3600; // ~70km/h fallback
      arrivalMs = Math.max(nowMs, t + travelSec * 1000); // credit time already driven
      t = arrivalMs;
    }
    const arrival = new Date(arrivalMs);
    t = t + dwell * 60000;
    prev = s;

    const plan = parseDbTime(planOf(s));
    const deltaMinutes = plan ? Math.round((arrivalMs - plan.getTime()) / 60000) : null;

    out.push({
      id: s.id,
      load_id: s.load_id,
      stop_type: s.stop_type,
      stop_sequence: s.stop_sequence,
      address: s.address,
      etaISO: arrival.toISOString(),
      etaFormatted: fmtTime(arrival),
      minutesFromNow: Math.round((arrivalMs - nowMs) / 60000),
      dwellMinutes: Math.round(dwell),
      planISO: plan ? plan.toISOString() : null,
      deltaMinutes,
      inProgress: !!(atStop && i === 0),
    });
  }
  return out;
};

/** All remaining stops across a driver's / truck's active loads, tagged with load id/number. */
const fetchRemainingAcrossLoads = async ({ truckId = null, driverId = null } = {}) => {
  if (!truckId && !driverId) return [];
  let q = supabase
    .from('loads')
    .select(`id, load_number, driver_id, truck_id, status, ${CARGO_COLS}, load_stops (*)`)
    .not('status', 'in', `(${DONE_LOAD_STATUSES.join(',')})`);
  q = truckId ? q.eq('truck_id', truckId) : q.eq('driver_id', driverId);
  const { data: loads, error } = await q;
  if (error || !loads) return [];
  const remaining = [];
  for (const l of loads) {
    for (const s of (l.load_stops || [])) {
      if (!stopIsDone(s)) remaining.push({ ...s, load_id: s.load_id || l.id, loadNumber: l.load_number, load: cargoLoad(l) });
    }
  }
  return remaining;
};

const gpsRefusal = (live) => ({
  success: false,
  reason: 'gps_stale',
  gps: live,
  message: `GPS is ${live.ageMinutes} min old - no live ETA until the truck reports again.`,
});

/**
 * Live ETAs for a single load's remaining stops, from the assigned truck's GPS.
 * The cascade runs over EVERY remaining stop on the driver's plan (all active
 * loads) so the times account for stops from other loads in between; only this
 * load's stops are returned.
 * @param {object} args.load  a load row incl. load_stops (or pass loadStops separately)
 * @param {array}  [args.loadStops]  stops if not on load
 * @param {object} [args.gps]  {lat,lng,timestamp,ageMinutes} to skip the GPS fetch
 * @param {boolean} [args.allowStale=false]  project even when GPS is OFFLINE (>30 min)
 * Returns { success, reason?, gps?, stops?, stale? }
 */
export const recalcLoadETAs = async ({ load, loadStops = null, gps = null, allowStale = false } = {}) => {
  try {
    if (!load) return { success: false, reason: 'no_load' };
    const ownStops = (loadStops || load.load_stops || []).map((s) => ({ ...s, load_id: s.load_id || load.id, load: cargoLoad(load) }));
    if (ownStops.filter((s) => !stopIsDone(s)).length === 0) return { success: false, reason: 'no_remaining' };

    const live = gps || await fetchLatestGps({ truckId: load.truck_id, driverId: load.driver_id });
    if (!live) return { success: false, reason: 'no_gps' };
    if (live.ageMinutes != null && live.ageMinutes > STALE_MAX_MINUTES && !allowStale) return gpsRefusal(live);

    // Merge this load's stops with the rest of the driver's plan (other loads).
    let all = ownStops;
    const others = await fetchRemainingAcrossLoads({ truckId: load.truck_id, driverId: load.driver_id });
    if (others.length > 0) {
      const ownIds = new Set(ownStops.map((s) => s.id));
      all = [...ownStops, ...others.filter((s) => !ownIds.has(s.id) && s.load_id !== load.id)];
    }
    const ordered = orderStops(all.filter((s) => !stopIsDone(s)));

    const projected = await cascadeFromGps(live, ordered);
    const mine = projected.filter((p) => p.load_id === load.id);
    if (mine.length === 0) return { success: false, reason: 'no_coords' };
    return {
      success: true,
      gps: live,
      stops: mine,
      stale: live.ageMinutes != null && live.ageMinutes > LIVE_MAX_MINUTES,
      generatedAt: new Date().toISOString(),
    };
  } catch (e) {
    console.error('recalcLoadETAs error:', e);
    return { success: false, reason: 'error', error: e.message };
  }
};

/**
 * The single next stop a truck/driver is headed to, with a live ETA - for the
 * fleet map. First remaining stop in plan order (in-progress stop first, then
 * planned arrival, else nearest by distance).
 * Returns { success, reason?, gps?, next? }  where next = one projected stop + loadNumber.
 */
export const nextStopETA = async ({ truckId = null, driverId = null, gps = null, allowStale = false } = {}) => {
  try {
    if (!truckId && !driverId) return { success: false, reason: 'no_target' };

    const live = gps || await fetchLatestGps({ truckId, driverId });
    if (!live) return { success: false, reason: 'no_gps' };
    if (live.ageMinutes != null && live.ageMinutes > STALE_MAX_MINUTES && !allowStale) return gpsRefusal(live);

    const remaining = await fetchRemainingAcrossLoads({ truckId, driverId });
    if (remaining.length === 0) return { success: false, reason: 'no_remaining' };

    const inProgress = remaining.find(stopInProgress);
    const withPlan = remaining.filter((s) => planOf(s));
    let next;
    if (inProgress) {
      next = inProgress;
    } else if (withPlan.length > 0) {
      next = orderStops(withPlan)[0];
    } else {
      const withCoords = [];
      for (const s of remaining) withCoords.push(await ensureCoords(s));
      const routable = withCoords.filter((s) => s.lat != null && s.lng != null);
      if (routable.length === 0) return { success: false, reason: 'no_coords' };
      next = routable.sort((a, b) =>
        haversineKm(live.lat, live.lng, a.lat, a.lng) -
        haversineKm(live.lat, live.lng, b.lat, b.lng))[0];
    }

    const projected = await cascadeFromGps(live, [next]);
    if (projected.length === 0) return { success: false, reason: 'no_coords' };
    return {
      success: true,
      gps: live,
      stale: live.ageMinutes != null && live.ageMinutes > LIVE_MAX_MINUTES,
      next: { ...projected[0], loadNumber: next.loadNumber },
    };
  } catch (e) {
    console.error('nextStopETA error:', e);
    return { success: false, reason: 'error', error: e.message };
  }
};

const liveEtaService = { fetchLatestGps, recalcLoadETAs, nextStopETA };
export default liveEtaService;
